/**
 * @fileoverview Dexie (IndexedDB) database for the Write-Ahead Queue (WAQ).
 * Stores pending mutations for offline/poor network resilience with retry/backoff.
 */

import DexieLib from 'dexie';

const hasIndexedDB = typeof indexedDB !== 'undefined';

function createMemoryTable() {
  const store = new Map();

  return {
    async clear() {
      store.clear();
    },

    /**
     * @param {Record<string, any>} record
     */
    async put(record) {
      if (!record || !record.id) {
        throw new Error('WAQ memory DB requires record.id');
      }
      store.set(record.id, { ...record });
      return record.id;
    },

    /**
     * @param {string} id
     */
    async delete(id) {
      store.delete(id);
    },

    /**
     * @param {string} id
     * @param {Record<string, any>} updates
     */
    async update(id, updates) {
      const existing = store.get(id);
      if (!existing) return 0;
      store.set(id, { ...existing, ...updates });
      return 1;
    },

    async toArray() {
      return Array.from(store.values()).map((item) => ({ ...item }));
    },

    /**
     * @param {string} field
     */
    where(field) {
      return {
        /**
         * @param {number} value
         */
        belowOrEqual(value) {
          return {
            async toArray() {
              return Array.from(store.values())
                .filter((item) => item[field] <= value)
                .map((item) => ({ ...item }));
            },
          };
        },
      };
    },
  };
}

function createMemoryDB() {
  const table = createMemoryTable();
  return {
    /**
     * @param {string} name
     */
    table(name) {
      if (name !== 'mutations') {
        throw new Error(`WAQ memory DB only supports the mutations table, got: ${name}`);
      }
      return table;
    },
  };
}

// Use Dexie when IndexedDB exists; otherwise fall back to in-memory shim.
export const waqDB = hasIndexedDB
  ? (() => {
      const db = new DexieLib('timegenie_waq');
      db.version(1).stores({
        // by status + nextAttemptAt for efficient draining
        mutations:
          '&id, status, nextAttemptAt, userId, type, createdAt, attemptCount, entityId, idempotencyKey',
      });
      return db;
    })()
  : createMemoryDB();

export default waqDB;
